import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { API } from '../config/api';
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import TestEnvBanner from "../components/TestEnvBanner";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  User,
  FileText,
  Building2,
  CalendarIcon,
  Loader2,
  FlaskConical,
  AlertCircle,
  CreditCard,
  Landmark,
  Phone,
  Bell,
  PoundSterling
} from "lucide-react";
import { format } from "date-fns";
import { cn } from "../lib/utils";
import ContactSearch from "../components/ContactSearch";
import SmartClientMatterSearch from "../components/SmartClientMatterSearch";
import useMatterPrefill from "../hooks/useMatterPrefill";
import SharePointMatterFeeEarner from "../components/SharePointMatterFeeEarner";
import DisbursementQuoteChoice, { choiceProblem } from "../components/DisbursementQuoteChoice";

import { LOGO_URL } from "../config/constants";
import { isInterAccount } from "../lib/paymentTypes";

export const STEPS = [
  { id: 1, title: "Payment Details", icon: CreditCard },
  { id: 2, title: "Matter & Account", icon: Building2 },
  { id: 3, title: "Payee & Payment", icon: Landmark },
  { id: 4, title: "Verification", icon: Phone },
  { id: 5, title: "Approval", icon: Bell },
  { id: 6, title: "Review", icon: Check }
];

export default function PaymentRequest() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // BM-H5023: disbursement quotes awaiting MOA on the chosen matter.
  const [awaitingQuotes, setAwaitingQuotes] = useState([]);
  
  const [formData, setFormData] = useState({
    form_type: "payment_request",
    // Step 1: Payment Details
    requester_name: "",
    requester_email: "",
    client_name: "",
    client_email: "",
    matter_reference: "",
    // Set when the picked matter came from SharePoint (pre-CM, no CM record).
    matter_source: "",
    responsible_fee_earner: "",
    // Brief 49 (13 Jul 2026): Payment Type dropdown — first field after
    // matter selection. Must be one of CTO / OTC / OTS / Third-Party.
    // No default; the user MUST pick.
    payment_type: "",
    // Step 2: Matter & Account
    account_type: "", // "Client Account" or "Office Account"
    invoice_saved: "",
    // Step 3: Payee & Payment
    payee_name: "",
    payee_reference: "",
    payee_bank: "",
    payee_account_number: "",
    payee_sort_code: "",
    payment_amount: "",
    payment_description: "",
    // Brief 49: inter-account transfer fields (used when payment_type is
    // CTO / OTC / OTS). Hidden and irrelevant on Third-Party.
    source_account: "",
    destination_account: "",
    internal_transfer_notes: "",
    // Step 4: Verbal Verification
    verbal_verification: "",
    verification_date: format(new Date(), "yyyy-MM-dd"),
    verification_contact: "",
    // BM-H3355: AML Policy §10.2, where the number called came from.
    verification_number_source: "",
    verification_not_from_request: false,
    no_verification_reason: "",
    // Step 5: Approval & Notifications
    notification_preference: "",
    is_urgent: "",
    urgent_reason: "",
    // Test flag
    is_test_submission: false
  });

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // BM-H3723: opened from a matter page (?matter=REF), start with that
  // matter filled in.
  const matterPrefillKey = useMatterPrefill(setFormData, ["client_name", "client_email", "responsible_fee_earner"]);

  const validateStep = (step) => {
    const errors = [];
    
    switch (step) {
      case 1:
        if (!formData.requester_name) errors.push("Requester name is required");
        if (!formData.requester_email) errors.push("Email is required");
        if (!formData.client_name) errors.push("Client name is required");
        if (!formData.matter_reference) errors.push("Matter reference is required");
        if (formData.matter_source === "sharepoint" && !formData.responsible_fee_earner)
          errors.push("Please choose a fee earner for this SharePoint matter");
        // Brief 49: Payment Type is required on Step 1 (first field
        // after matter selection).
        if (!formData.payment_type) errors.push("Please pick a payment type");
        {
          const dq = choiceProblem(awaitingQuotes, formData.disbursement_quote_id);
          if (dq) errors.push(dq);
        }
        break;
      case 2:
        if (!formData.account_type) errors.push("Please select account type");
        if (!formData.invoice_saved) errors.push("Please confirm invoice status");
        break;
      case 3:
        // Brief 49: field set depends on payment_type. Inter-account
        // (CTO / OTC / OTS) requires source_account, destination_account,
        // internal_transfer_notes. Third-Party keeps the pre-Brief-49
        // payee-side requirements. Amount + description are required
        // on both paths.
        if (isInterAccount(formData.payment_type)) {
          if (!formData.source_account) errors.push("Source account is required");
          if (!formData.destination_account) errors.push("Destination account is required");
          if (!formData.internal_transfer_notes) errors.push("Internal transfer notes are required");
        } else {
          if (!formData.payee_name) errors.push("Payee name is required");
          if (!formData.payee_bank) errors.push("Payee bank is required");
          if (!formData.payee_account_number) errors.push("Account number is required");
          if (!formData.payee_sort_code) errors.push("Sort code is required");
        }
        if (!formData.payment_amount) errors.push("Payment amount is required");
        if (!formData.payment_description) errors.push("Payment description is required");
        break;
      case 4:
        if (!formData.verbal_verification) errors.push("Please confirm verbal verification status");
        if (formData.verbal_verification === "Yes") {
          if (!formData.verification_date) errors.push("Verification date is required");
          if (!formData.verification_contact) errors.push("Verification contact is required");
          // BM-H3355: AML Policy §10.2 (inter-account transfers have no payee to call).
          if (!isInterAccount(formData.payment_type)) {
            if (!formData.verification_number_source) errors.push("Say where the number you called came from");
            if (!formData.verification_not_from_request) {
              errors.push("Confirm the number did not come from the email or letter giving the bank details");
            }
          }
        } else if (formData.verbal_verification === "No") {
          if (!isInterAccount(formData.payment_type)) {
            // BM-H3361: AML Policy §10.2, no third-party payment until the details are verified.
            errors.push("A third-party payment cannot be requested until the bank details have been verified by phone");
          } else if (!formData.no_verification_reason) {
            errors.push("Please explain why verification was not made");
          }
        }
        break;
      case 5:
        if (!formData.notification_preference) errors.push("Please select notification preference");
        if (!formData.is_urgent) errors.push("Please indicate if payment is urgent");
        if (formData.is_urgent === "Yes" && !formData.urgent_reason) {
          errors.push("Please explain why this payment is urgent");
        }
        break;
      default:
        break;
    }
    
    return errors;
  };

  const handleNext = () => {
    const errors = validateStep(currentStep);
    if (errors.length > 0) {
      errors.forEach(e => toast.error(e));
      return;
    }
    setCurrentStep(prev => Math.min(prev + 1, STEPS.length));
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/submissions`, {
        data: formData
      });

      toast.success("Payment request submitted successfully!");

      // Brief 11c-followup: surface the SharePoint audit-trail outcome
      // (added by 11c on the BE response). Closes the confidence loop
      // visibly — until now the user had no signal whether the PDF
      // actually filed. Branches:
      //   success === true            -> toast.success (filename subtitle)
      //   skipped === true            -> toast.info (notification still sent;
      //                                  PDF gen failed; admin notified)
      //   success === false (no skip) -> toast.warning (filing failed;
      //                                  user knows to flag)
      //   undefined                   -> no-op (pre-11c-deploy submit or
      //                                  invoice_request response shape)
      const sp = response?.data?.sp_filing_status;
      if (sp?.success === true) {
        toast.success("Filed to Compliance and Financials/", {
          description: sp.filename || undefined,
          duration: 6000,
          // data-testid via id maps through sonner's DOM in tests.
          id: "sp-filing-toast-success",
        });
      } else if (sp?.skipped === true && sp?.reason === "empty_pdf_bytes") {
        toast.info(
          "Notification sent. PDF couldn't be generated - admin notified.",
          {
            duration: 6000,
            id: "sp-filing-toast-skipped",
          }
        );
      } else if (sp && sp.success === false && !sp.skipped) {
        toast.warning(
          "Notification sent, but the PDF couldn't be filed to SharePoint.",
          {
            description: sp.error || undefined,
            duration: 8000,
            id: "sp-filing-toast-warning",
          }
        );
      }

      navigate(`/submission/${response.data.id}`);
    } catch (error) {
      console.error("Submission error:", error);
      // BM-H3355: show the server's reason (e.g. AML Policy §10.2) when it gives one.
      const detail = error?.response?.data?.detail;
      const message = (detail && (detail.message || (typeof detail === "string" ? detail : ""))) || "";
      toast.error(message || "Failed to submit payment request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderProgressBar = () => (
    <div className="mb-8">
      <div className="flex items-center justify-between">
        {STEPS.map((step, index) => {
          const Icon = step.icon;
          const isCompleted = currentStep > step.id;
          const isCurrent = currentStep === step.id;
          
          return (
            <div key={step.id} className="flex items-center">
              <div className="flex flex-col items-center">
                <div
                  className={cn(
                    "w-10 h-10 rounded-md flex items-center justify-center border-2 transition-all",
                    isCompleted && "bg-teal border-teal text-white",
                    isCurrent && "bg-white border-teal text-teal",
                    !isCompleted && !isCurrent && "bg-gray-100 border-gray-300 text-gray-400"
                  )}
                >
                  {isCompleted ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                </div>
                <span className={cn(
                  "mt-2 text-xs font-medium hidden sm:block",
                  isCurrent ? "text-teal" : "text-gray-500"
                )}>
                  {step.title}
                </span>
              </div>
              {index < STEPS.length - 1 && (
                <div className={cn(
                  "w-12 md:w-16 h-0.5 mx-2",
                  currentStep > step.id ? "bg-teal" : "bg-gray-200"
                )} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F5F5F0]">
      <TestEnvBanner />
      {/* Header */}
      <header className="glass-nav sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Bonsai Law" className="h-10 w-auto" />
          </Link>
          <h1 className="text-lg font-semibold text-gray-800">Payment Request</h1>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-4 py-8">
        {renderProgressBar()}

        <div className="glass-card rounded-md p-6 md:p-8">
          {/* Step Content */}
          {currentStep === 1 && <Step1PaymentDetails setAwaitingQuotes={setAwaitingQuotes} formData={formData} matterPrefillKey={matterPrefillKey} updateField={updateField} updateFields={(fields) => setFormData(prev => ({ ...prev, ...fields }))} />}
          {currentStep === 2 && <Step2MatterAccount formData={formData} updateField={updateField} />}
          {currentStep === 3 && <Step3PayeePayment formData={formData} updateField={updateField} />}
          {currentStep === 4 && <Step4VerbalVerification formData={formData} updateField={updateField} />}
          {currentStep === 5 && <Step5ApprovalNotifications formData={formData} updateField={updateField} />}
          {currentStep === 6 && <Step6Review formData={formData} />}

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
            <Button
              variant="outline"
              onClick={currentStep === 1 ? () => navigate("/") : handleBack}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              {currentStep === 1 ? "Cancel" : "Back"}
            </Button>

            {currentStep < STEPS.length ? (
              <Button
                onClick={handleNext}
                className="bg-teal hover:bg-teal-hover text-white flex items-center gap-2"
              >
                Next
                <ArrowRight className="w-4 h-4" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="bg-gold hover:bg-gold text-white flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Submit Payment Request
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

// Step 1: Payment Details
// BM-H3862: matterPrefillKey comes in as a prop; it lives in the page component.
function Step1PaymentDetails({ formData, matterPrefillKey, updateField, updateFields, setAwaitingQuotes }) {
  const handleContactSelect = (data) => {
    const fields = {};
    if (data.client_name !== undefined) fields.client_name = data.client_name;
    if (data.client_email !== undefined) fields.client_email = data.client_email;
    if (data.matter_reference !== undefined) fields.matter_reference = data.matter_reference;
    updateFields(fields);
  };

  const handleContactClear = () => {
    updateFields({ client_name: "", client_email: "" });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-teal mb-2">Payment Details</h2>
        <p className="text-gray-600 text-sm">Basic information about this payment request</p>
      </div>

      <div className="grid gap-4">
        <div>
          <Label htmlFor="requester_name" className="text-sm font-medium">
            Who is completing this request? <span className="text-status-red">*</span>
          </Label>
          <Input
            id="requester_name"
            value={formData.requester_name}
            onChange={(e) => updateField("requester_name", e.target.value)}
            placeholder="Enter your name"
            className="mt-1"
            data-testid="requester-name-input"
          />
        </div>

        <div>
          <Label htmlFor="requester_email" className="text-sm font-medium">
            Your email address <span className="text-status-red">*</span>
          </Label>
          <Input
            id="requester_email"
            type="email"
            value={formData.requester_email}
            onChange={(e) => updateField("requester_email", e.target.value)}
            placeholder="you@bonsai.law"
            className="mt-1"
            data-testid="requester-email-input"
          />
        </div>

        <ContactSearch
          label="Client name"
          selectedName={formData.client_name}
          selectedEmail={formData.client_email}
          onSelect={handleContactSelect}
          onClear={handleContactClear}
        />

        <div>
          <Label htmlFor="matter_reference" className="text-sm font-medium">
            Matter reference number <span className="text-status-red">*</span>
          </Label>
          {/* Brief #4 — SmartClientMatterSearch rollout (Block 3 table
              row #5: feeEarnerScoped=true; the component does not expose
              a scoping prop, intent captured here as code intent). */}
          <SmartClientMatterSearch
            key={matterPrefillKey}
            label={null}
            placeholder="e.g., invoice number / PO / file ref"
            initialValue={formData.matter_reference}
            onSelect={(result) => {
              const ref = result.matter_reference || result.label || "";
              updateField("matter_reference", ref);
              if (!formData.client_name && result.client_name) {
                updateField("client_name", result.client_name);
              }
              // SharePoint-only matters carry no fee earner — flag the source
              // so the prompt below appears. CM matters clear it.
              updateField("matter_source", result.source || "");
              if (result.source !== "sharepoint" && result.fee_earner) {
                updateField("responsible_fee_earner", result.fee_earner);
              }
            }}
            className="mt-1"
          />
          <SharePointMatterFeeEarner
            source={formData.matter_source}
            value={formData.responsible_fee_earner}
            onChange={(name) => updateField("responsible_fee_earner", name)}
          />
        </div>

        {/* Brief 49 (13 Jul 2026): Payment Type dropdown — first field
            after matter selection. Closed set: CTO / OTC / OTS / Third-Party.
            No default; the user must pick. Inter-account types swap the
            Step 3 payee-side field set for source/destination/notes. */}
        <div>
          <Label htmlFor="payment_type" className="text-sm font-medium">
            Payment type <span className="text-status-red">*</span>
          </Label>
          <select
            id="payment_type"
            value={formData.payment_type}
            onChange={(e) => updateField("payment_type", e.target.value)}
            className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-teal focus:outline-none focus:ring-1 focus:ring-teal"
            data-testid="payment-type-select"
          >
            <option value="">— pick one —</option>
            <option value="CTO">CTO — Client to Other</option>
            <option value="OTC">OTC — Office to Client</option>
            <option value="OTS">OTS — Office to Something</option>
            <option value="Third-Party">Third-Party — external payee</option>
          </select>
          <p className="text-xs text-gray-500 mt-1" data-testid="payment-type-tooltip">
            {formData.payment_type === "CTO" && "CTO — Client to Other (client account outgoing)"}
            {formData.payment_type === "OTC" && "OTC — Office to Client (office to client account)"}
            {formData.payment_type === "OTS" && "OTS — Office to Something (internal office movement)"}
            {formData.payment_type === "Third-Party" && "Third-Party — payment to an external party"}
            {!formData.payment_type && "Pick a type. Inter-account transfers (CTO / OTC / OTS) hide the payee bank fields; Third-Party keeps them."}
          </p>
          <DisbursementQuoteChoice
            matterRef={formData.matter_reference}
            paymentType={formData.payment_type}
            value={formData.disbursement_quote_id || ""}
            onChange={(v) => updateField("disbursement_quote_id", v)}
            onQuotes={setAwaitingQuotes}
          />
        </div>
      </div>

      {/* Test Submission Flag */}
      <div className="p-4 bg-status-amber-dim border border-amber-200 rounded-md mt-6">
        <div className="flex items-start space-x-3">
          <Checkbox
            id="is_test_submission"
            checked={formData.is_test_submission}
            onCheckedChange={(checked) => updateField("is_test_submission", checked)}
            className="mt-0.5 border-amber-400 data-[state=checked]:bg-status-amber"
            data-testid="test-submission-checkbox"
          />
          <div>
            <Label htmlFor="is_test_submission" className="font-medium text-status-amber cursor-pointer flex items-center gap-2">
              <FlaskConical className="w-4 h-4" />
              This is a test submission (for internal pilot only)
            </Label>
            <p className="text-xs text-status-amber mt-1">
              Test submissions will be marked with [TEST] and can be filtered/deleted later.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Step 2: Matter & Account
function Step2MatterAccount({ formData, updateField }) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-teal mb-2">Matter & Account</h2>
        <p className="text-gray-600 text-sm">Specify the payment source and confirm invoice storage</p>
      </div>

      <div className="space-y-6">
        <div>
          <Label className="text-sm font-medium">
            Payment from office or client account? <span className="text-status-red">*</span>
          </Label>
          <div className="grid grid-cols-2 gap-4 mt-3">
            <button
              type="button"
              onClick={() => updateField("account_type", "Client Account")}
              className={cn(
                "p-4 rounded-md border-2 text-center transition-all",
                formData.account_type === "Client Account"
                  ? "border-teal bg-teal/5 text-teal"
                  : "border-gray-200 hover:border-gray-300 text-gray-700"
              )}
              data-testid="account-type-client"
            >
              <Building2 className="w-6 h-6 mx-auto mb-2" />
              <span className="font-medium">Client Account</span>
            </button>
            <button
              type="button"
              onClick={() => updateField("account_type", "Office Account")}
              className={cn(
                "p-4 rounded-md border-2 text-center transition-all",
                formData.account_type === "Office Account"
                  ? "border-teal bg-teal/5 text-teal"
                  : "border-gray-200 hover:border-gray-300 text-gray-700"
              )}
              data-testid="account-type-office"
            >
              <Landmark className="w-6 h-6 mx-auto mb-2" />
              <span className="font-medium">Office Account</span>
            </button>
          </div>
        </div>

        <div>
          <Label className="text-sm font-medium">
            Have you saved the invoice into the matter Financials folder? <span className="text-status-red">*</span>
          </Label>
          <RadioGroup
            value={formData.invoice_saved}
            onValueChange={(value) => updateField("invoice_saved", value)}
            className="mt-3 space-y-2"
          >
            <div className="flex items-center space-x-3 p-3 rounded-md border border-gray-200 hover:bg-gray-50">
              <RadioGroupItem value="Yes" id="invoice_saved_yes" data-testid="invoice-saved-yes" />
              <Label htmlFor="invoice_saved_yes" className="cursor-pointer flex-1">Yes</Label>
            </div>
            <div className="flex items-center space-x-3 p-3 rounded-md border border-gray-200 hover:bg-gray-50">
              <RadioGroupItem value="No" id="invoice_saved_no" data-testid="invoice-saved-no" />
              <Label htmlFor="invoice_saved_no" className="cursor-pointer flex-1">No</Label>
            </div>
          </RadioGroup>
        </div>
      </div>
    </div>
  );
}

// Step 3: Payee & Payment
// Brief 49 (13 Jul 2026): conditional field set driven by payment_type.
//   * Inter-account (CTO / OTC / OTS) — hide payee_name / payee_bank /
//     payee_account_number / payee_sort_code / payee_reference; show
//     source_account, destination_account, internal_transfer_notes.
//   * Third-Party — restore the pre-Brief-49 payee-side fields.
// Amount + description are collected on both paths.
function Step3PayeePayment({ formData, updateField }) {
  const interAccount = isInterAccount(formData.payment_type);
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-teal mb-2">
          {interAccount ? "Inter-account transfer" : "Payee & Payment"}
        </h2>
        <p className="text-gray-600 text-sm" data-testid="step3-subtitle">
          {interAccount
            ? "Specify source and destination accounts for this internal transfer."
            : "Enter payee bank details and payment information."}
        </p>
        {formData.payment_type && (
          <div
            data-testid="step3-payment-type-tag"
            className="inline-block mt-2 px-2 py-0.5 text-xs rounded bg-teal/10 text-teal"
          >
            Payment type: {formData.payment_type}
          </div>
        )}
      </div>

      <div className="grid gap-4">
        {interAccount ? (
          <>
            <div data-testid="inter-account-fields">
              <Label htmlFor="source_account" className="text-sm font-medium">
                Source account <span className="text-status-red">*</span>
              </Label>
              <Input
                id="source_account"
                value={formData.source_account}
                onChange={(e) => updateField("source_account", e.target.value)}
                placeholder="e.g., Office account / matter ref of source"
                className="mt-1"
                data-testid="source-account-input"
              />
            </div>

            <div>
              <Label htmlFor="destination_account" className="text-sm font-medium">
                Destination account <span className="text-status-red">*</span>
              </Label>
              <Input
                id="destination_account"
                value={formData.destination_account}
                onChange={(e) => updateField("destination_account", e.target.value)}
                placeholder="e.g., Client account / matter ref of destination"
                className="mt-1"
                data-testid="destination-account-input"
              />
            </div>

            <div>
              <Label htmlFor="internal_transfer_notes" className="text-sm font-medium">
                Internal transfer notes <span className="text-status-red">*</span>
              </Label>
              <Textarea
                id="internal_transfer_notes"
                value={formData.internal_transfer_notes}
                onChange={(e) => updateField("internal_transfer_notes", e.target.value)}
                placeholder="Reason for the transfer, ledger references, etc."
                className="mt-1 min-h-[100px]"
                data-testid="internal-transfer-notes-input"
              />
            </div>
          </>
        ) : (
          <>
            <div data-testid="third-party-fields">
              <Label htmlFor="payee_name" className="text-sm font-medium">
                Payee name <span className="text-status-red">*</span>
              </Label>
              <Input
                id="payee_name"
                value={formData.payee_name}
                onChange={(e) => updateField("payee_name", e.target.value)}
                placeholder="Name of the person/company to pay"
                className="mt-1"
                data-testid="payee-name-input"
              />
            </div>

            <div>
              <Label htmlFor="payee_reference" className="text-sm font-medium">
                Payee reference
              </Label>
              <Input
                id="payee_reference"
                value={formData.payee_reference}
                onChange={(e) => updateField("payee_reference", e.target.value)}
                placeholder="Client ID or project code (optional)"
                className="mt-1"
                data-testid="payee-reference-input"
              />
              <p className="text-xs text-gray-500 mt-1">Optional</p>
            </div>

            <div>
              <Label htmlFor="payee_bank" className="text-sm font-medium">
                Payee bank <span className="text-status-red">*</span>
              </Label>
              <Input
                id="payee_bank"
                value={formData.payee_bank}
                onChange={(e) => updateField("payee_bank", e.target.value)}
                placeholder="e.g., Barclays, HSBC, Lloyds"
                className="mt-1"
                data-testid="payee-bank-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="payee_account_number" className="text-sm font-medium">
                  Account number <span className="text-status-red">*</span>
                </Label>
                <Input
                  id="payee_account_number"
                  value={formData.payee_account_number}
                  onChange={(e) => updateField("payee_account_number", e.target.value)}
                  placeholder="12345678"
                  className="mt-1"
                  data-testid="payee-account-input"
                />
              </div>
              <div>
                <Label htmlFor="payee_sort_code" className="text-sm font-medium">
                  Sort code <span className="text-status-red">*</span>
                </Label>
                <Input
                  id="payee_sort_code"
                  value={formData.payee_sort_code}
                  onChange={(e) => updateField("payee_sort_code", e.target.value)}
                  placeholder="12-34-56"
                  className="mt-1"
                  data-testid="payee-sortcode-input"
                />
              </div>
            </div>
            <p className="text-xs text-gray-500 -mt-2">Double-check account number and sort code for accuracy</p>
          </>
        )}

        <div>
          <Label htmlFor="payment_amount" className="text-sm font-medium">
            Payment amount <span className="text-status-red">*</span>
          </Label>
          <div className="relative mt-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">£</span>
            <Input
              id="payment_amount"
              type="text"
              value={formData.payment_amount}
              onChange={(e) => updateField("payment_amount", e.target.value)}
              placeholder="0.00"
              className="pl-7"
              data-testid="payment-amount-input"
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">Gross amount including VAT, if applicable</p>
        </div>

        <div>
          <Label htmlFor="payment_description" className="text-sm font-medium">
            Payment description <span className="text-status-red">*</span>
          </Label>
          <Textarea
            id="payment_description"
            value={formData.payment_description}
            onChange={(e) => updateField("payment_description", e.target.value)}
            placeholder="Include invoice number and what this payment relates to"
            className="mt-1 min-h-[100px]"
            data-testid="payment-description-input"
          />
        </div>
      </div>
    </div>
  );
}

// Step 4: Verbal Verification
export function Step4VerbalVerification({ formData, updateField }) { // exported for BM-H3355 tests
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-teal mb-2">Verbal Verification</h2>
        <p className="text-gray-600 text-sm">Confirm verification of payee bank details</p>
      </div>

      <div className="space-y-6">
        <div>
          <Label className="text-sm font-medium">
            Has verbal verification of account details been made? <span className="text-status-red">*</span>
          </Label>
          <RadioGroup
            value={formData.verbal_verification}
            onValueChange={(value) => updateField("verbal_verification", value)}
            className="mt-3 space-y-2"
          >
            <div className="flex items-center space-x-3 p-3 rounded-md border border-gray-200 hover:bg-gray-50">
              <RadioGroupItem value="Yes" id="verbal_yes" data-testid="verbal-verification-yes" />
              <Label htmlFor="verbal_yes" className="cursor-pointer flex-1">Yes</Label>
            </div>
            <div className="flex items-center space-x-3 p-3 rounded-md border border-gray-200 hover:bg-gray-50">
              <RadioGroupItem value="No" id="verbal_no" data-testid="verbal-verification-no" />
              <Label htmlFor="verbal_no" className="cursor-pointer flex-1">No</Label>
            </div>
          </RadioGroup>
        </div>

        {/* Conditional: Yes - show verification details */}
        {formData.verbal_verification === "Yes" && (
          <div className="space-y-4 p-4 bg-status-green-dim rounded-md border border-green-200">
            <h3 className="font-medium text-status-green flex items-center gap-2">
              <Phone className="w-4 h-4" />
              Verification Details
            </h3>
            
            <div>
              <Label htmlFor="verification_date" className="text-sm font-medium text-status-green">
                Date of verification <span className="text-status-red">*</span>
              </Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal mt-1 bg-white",
                      !formData.verification_date && "text-muted-foreground"
                    )}
                    data-testid="verification-date-picker"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.verification_date ? format(new Date(formData.verification_date), "PPP") : "Select date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.verification_date ? new Date(formData.verification_date) : undefined}
                    onSelect={(date) => updateField("verification_date", date ? format(date, "yyyy-MM-dd") : "")}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <Label htmlFor="verification_contact" className="text-sm font-medium text-status-green">
                Verification contact <span className="text-status-red">*</span>
              </Label>
              <Input
                id="verification_contact"
                value={formData.verification_contact}
                onChange={(e) => updateField("verification_contact", e.target.value)}
                placeholder="Name of person you spoke to"
                className="mt-1 bg-white"
                data-testid="verification-contact-input"
              />
            </div>

            {/* BM-H3355: AML Policy §10.2. The number must not come from the request
                that gave the bank details. */}
            {!isInterAccount(formData.payment_type) && (
              <div className="space-y-2" data-testid="verification-number-source">
                <Label className="text-sm font-medium text-status-green">
                  Where did the number you called come from? <span className="text-status-red">*</span>
                </Label>
                <RadioGroup
                  value={formData.verification_number_source}
                  onValueChange={(value) => updateField("verification_number_source", value)}
                  className="space-y-1"
                >
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="on_file" id="number_on_file" data-testid="number-source-on-file" />
                    <Label htmlFor="number_on_file" className="cursor-pointer">A number already on the file</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="independent" id="number_independent" data-testid="number-source-independent" />
                    <Label htmlFor="number_independent" className="cursor-pointer">
                      An independent source, such as the payee's own website
                    </Label>
                  </div>
                </RadioGroup>
                <div className="flex items-start gap-2 pt-1">
                  <Checkbox
                    id="verification_not_from_request"
                    checked={!!formData.verification_not_from_request}
                    onCheckedChange={(v) => updateField("verification_not_from_request", v === true)}
                    data-testid="number-not-from-request"
                  />
                  <Label htmlFor="verification_not_from_request" className="text-sm cursor-pointer">
                    I did not use a number from the email or letter that gave the bank details.
                  </Label>
                </div>
              </div>
            )}
          </div>
        )}

        {/* BM-H3361: AML Policy §10.2, a third-party payment cannot go ahead unverified. */}
        {formData.verbal_verification === "No" && !isInterAccount(formData.payment_type) && (
          <div className="p-4 bg-status-red-dim rounded-md border border-red-300 text-sm text-status-red"
            data-testid="unverified-third-party-blocked">
            AML Policy §10.2: no money is paid to a third party until the bank details have been verified by
            phone, on a number already on the file or from an independent source. Verify them, then answer Yes.
          </div>
        )}

        {/* Conditional: No - show explanation field (transfers between the firm's own accounts) */}
        {formData.verbal_verification === "No" && isInterAccount(formData.payment_type) && (
          <div className="space-y-4 p-4 bg-status-amber-dim rounded-md border border-amber-200">
            <div className="flex items-center gap-2 text-status-amber">
              <AlertCircle className="w-5 h-5" />
              <span className="font-medium">Verification Not Made</span>
            </div>
            
            <div>
              <Label htmlFor="no_verification_reason" className="text-sm font-medium text-status-amber">
                If no verbal verification was made, please explain why <span className="text-status-red">*</span>
              </Label>
              <Textarea
                id="no_verification_reason"
                value={formData.no_verification_reason}
                onChange={(e) => updateField("no_verification_reason", e.target.value)}
                placeholder="Please provide a reason..."
                className="mt-1 min-h-[100px] bg-white"
                data-testid="no-verification-reason-input"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Step 5: Approval & Notifications
function Step5ApprovalNotifications({ formData, updateField }) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-teal mb-2">Approval & Notifications</h2>
        <p className="text-gray-600 text-sm">Set notification preferences and urgency</p>
      </div>

      <div className="space-y-6">
        <div>
          <Label className="text-sm font-medium">
            Would you like to be notified by email when the payment has been made? <span className="text-status-red">*</span>
          </Label>
          <RadioGroup
            value={formData.notification_preference}
            onValueChange={(value) => updateField("notification_preference", value)}
            className="mt-3 space-y-2"
          >
            <div className="flex items-center space-x-3 p-3 rounded-md border border-gray-200 hover:bg-gray-50">
              <RadioGroupItem value="Yes" id="notify_yes" data-testid="notify-yes" />
              <Label htmlFor="notify_yes" className="cursor-pointer flex-1">Yes, notify me when payment is made</Label>
            </div>
            <div className="flex items-center space-x-3 p-3 rounded-md border border-gray-200 hover:bg-gray-50">
              <RadioGroupItem value="No" id="notify_no" data-testid="notify-no" />
              <Label htmlFor="notify_no" className="cursor-pointer flex-1">No notification required</Label>
            </div>
          </RadioGroup>
        </div>

        <div>
          <Label className="text-sm font-medium">
            Is this payment urgent? <span className="text-status-red">*</span>
          </Label>
          <RadioGroup
            value={formData.is_urgent}
            onValueChange={(value) => updateField("is_urgent", value)}
            className="mt-3 space-y-2"
          >
            <div className="flex items-center space-x-3 p-3 rounded-md border border-gray-200 hover:bg-gray-50">
              <RadioGroupItem value="Yes" id="urgent_yes" data-testid="urgent-yes" />
              <Label htmlFor="urgent_yes" className="cursor-pointer flex-1">Yes, this is urgent</Label>
            </div>
            <div className="flex items-center space-x-3 p-3 rounded-md border border-gray-200 hover:bg-gray-50">
              <RadioGroupItem value="No" id="urgent_no" data-testid="urgent-no" />
              <Label htmlFor="urgent_no" className="cursor-pointer flex-1">No, standard processing</Label>
            </div>
          </RadioGroup>
        </div>

        {/* Conditional: Urgent = Yes - show reason */}
        {formData.is_urgent === "Yes" && (
          <div className="p-4 bg-status-red-dim rounded-md border border-red-200">
            <Label htmlFor="urgent_reason" className="text-sm font-medium text-status-red">
              If urgent, why? <span className="text-status-red">*</span>
            </Label>
            <Textarea
              id="urgent_reason"
              value={formData.urgent_reason}
              onChange={(e) => updateField("urgent_reason", e.target.value)}
              placeholder="Please explain why this payment is urgent..."
              className="mt-1 min-h-[80px] bg-white"
              data-testid="urgent-reason-input"
            />
          </div>
        )}
      </div>
    </div>
  );
}

// Step 6: Review & Submit
function Step6Review({ formData }) {
  const ReviewSection = ({ title, children }) => (
    <div className="mb-6">
      <h3 className="text-sm font-semibold text-teal uppercase tracking-wide mb-3">{title}</h3>
      <div className="bg-gray-50 rounded-md p-4 space-y-2">
        {children}
      </div>
    </div>
  );

  const ReviewField = ({ label, value, highlight = false }) => (
    <div className="flex justify-between py-1">
      <span className="text-gray-600 text-sm">{label}</span>
      <span className={cn("text-sm font-medium text-right max-w-[60%]", highlight && "text-gold")}>{value || "—"}</span>
    </div>
  );

  // Format amount for display
  const formatAmount = (amount) => {
    if (!amount) return "—";
    const num = parseFloat(amount.replace(/[^0-9.]/g, ''));
    if (isNaN(num)) return amount;
    return `£${num.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-teal mb-2">Review & Submit</h2>
        <p className="text-gray-600 text-sm">Please review all details before submitting</p>
      </div>

      {formData.is_test_submission && (
        <div className="p-3 bg-status-amber-dim border border-amber-200 rounded-md flex items-center gap-2">
          <FlaskConical className="w-5 h-5 text-status-amber" />
          <span className="text-status-amber font-medium">This is a TEST submission</span>
        </div>
      )}

      {formData.is_urgent === "Yes" && (
        <div className="p-3 bg-status-red-dim border border-red-200 rounded-md flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-status-red" />
          <span className="text-status-red font-medium">URGENT PAYMENT</span>
        </div>
      )}

      <ReviewSection title="Payment Details">
        <ReviewField label="Requester" value={formData.requester_name} />
        <ReviewField label="Email" value={formData.requester_email} />
        <ReviewField label="Client Name" value={formData.client_name} />
        <ReviewField label="Matter Reference" value={formData.matter_reference} highlight />
      </ReviewSection>

      <ReviewSection title="Matter & Account">
        <ReviewField label="Account Type" value={formData.account_type} highlight />
        <ReviewField label="Invoice Saved to Financials" value={formData.invoice_saved} />
      </ReviewSection>

      <ReviewSection title="Payee & Payment">
        <ReviewField label="Payee Name" value={formData.payee_name} />
        <ReviewField label="Payee Reference" value={formData.payee_reference} />
        <ReviewField label="Payee Bank" value={formData.payee_bank} />
        <ReviewField label="Account Number" value={formData.payee_account_number} />
        <ReviewField label="Sort Code" value={formData.payee_sort_code} />
        <ReviewField label="Amount" value={formatAmount(formData.payment_amount)} highlight />
        <ReviewField label="Description" value={formData.payment_description} />
      </ReviewSection>

      <ReviewSection title="Verbal Verification">
        <ReviewField label="Verification Made" value={formData.verbal_verification} highlight={formData.verbal_verification === "No"} />
        {formData.verbal_verification === "Yes" ? (
          <>
            <ReviewField label="Verification Date" value={formData.verification_date} />
            <ReviewField label="Contact Person" value={formData.verification_contact} />
            {formData.verification_number_source ? (
              <ReviewField
                label="Number Came From"
                value={formData.verification_number_source === "on_file" ? "Already on the file" : "An independent source"}
              />
            ) : null}
          </>
        ) : (
          <ReviewField label="Reason Not Verified" value={formData.no_verification_reason} />
        )}
      </ReviewSection>

      <ReviewSection title="Approval & Notifications">
        <ReviewField label="Notification Preference" value={formData.notification_preference === "Yes" ? "Notify when paid" : "No notification"} />
        <ReviewField label="Urgent Payment" value={formData.is_urgent} highlight={formData.is_urgent === "Yes"} />
        {formData.is_urgent === "Yes" && (
          <ReviewField label="Urgency Reason" value={formData.urgent_reason} />
        )}
      </ReviewSection>

      <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <p className="text-blue-800 font-medium">Ready to submit?</p>
            <p className="text-blue-700 text-sm mt-1">
              Once submitted, this request will be sent to Accounts for processing. 
              You will receive a confirmation email with a PDF summary.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
